import CreateAllergyDialog from '@client/page/management/menu/allergy/component/create-allergy-dialog.tsx';
import useGetAllergies from '@client/page/management/menu/allergy/logic/use-get-allergies.ts';
import { useCompanyId } from '@packages/core/company/focused-company-context.ts';
import { useModalManagerContext } from '@packages/ui/modal/modal-manager-context.ts';
import { useTranslate } from '@tolgee/react';
import CheckBoxInput from '@packages/ui/form/input/check-box-input';
import { useMemo } from 'react';
import SecondaryButton from '@packages/ui/button/secondary-button';

export default function AllergyInput(props: {
  allergyString: string;
  handleEachAllergyCode: (value: string) => void;
  errorMessage?: string;
}) {
  const { allergyString, handleEachAllergyCode, errorMessage } = props;

  // Services
  const companyId = useCompanyId();
  const { t } = useTranslate();
  const dialogManager = useModalManagerContext();

  // Queries
  const { data } = useGetAllergies(companyId);

  // Parse current allergies from allergyString
  const currentAllergies = useMemo(() => {
    return allergyString
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter((item) => item !== '');
  }, [allergyString]);

  // Check if an allergy code is already selected
  const isAllergySelected = (code: string) => {
    return currentAllergies.includes(code.toLowerCase());
  };

  // Handle checkbox change
  const handleCheckboxChange = (checked: boolean, allergyCode: string) => {
    if (checked) {
      // Add allergy code if not already present
      if (!isAllergySelected(allergyCode)) {
        const updatedAllergyString = [...currentAllergies, allergyCode].join(', ');
        handleEachAllergyCode(updatedAllergyString + (updatedAllergyString ? ', ' : ''));
      }
    } else {
      // Remove allergy code if present
      const updatedAllergies = currentAllergies.filter((code) => code.toLowerCase() !== allergyCode.toLowerCase());
      const updatedAllergyString = updatedAllergies.join(', ');
      handleEachAllergyCode(updatedAllergyString + (updatedAllergyString ? ', ' : ''));
    }
  };

  const addNewAllergy = (value: string) => {
    const lowerCaseValue = value.toLowerCase();

    for (const allergy of currentAllergies) {
      if (allergy == lowerCaseValue) {
        return;
      }
    }

    const updatedAllergyString = currentAllergies.concat(value).join(', ') + ', ';
    handleEachAllergyCode(updatedAllergyString);
  };

  const createAllergyDialog = () => {
    dialogManager.showDialog((onClose) => <CreateAllergyDialog addNewAllergy={addNewAllergy} onClose={onClose} />);
  };

  // Split allergies into two columns
  const allergiesColumns = useMemo(() => {
    if (!data?.allergies || data.allergies.length === 0) return [[], []];

    const allergies = [...data.allergies];
    const midpoint = Math.ceil(allergies.length / 2);

    return [allergies.slice(0, midpoint), allergies.slice(midpoint)];
  }, [data?.allergies]);

  return (
    <div className="relative w-full border rounded-md">
      <div className="p-2 border-b bg-gray-100">
        <SecondaryButton
          onClick={createAllergyDialog}
          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
        >
          {t('allergy.add_new', 'Add new')}
        </SecondaryButton>
      </div>

      <div className="p-2 flex flex-col space-y-4 max-h-48 overflow-y-scroll">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2">
            {allergiesColumns[0]?.map((allergy) => (
              <CheckBoxInput
                key={allergy.code}
                value={isAllergySelected(allergy.code)}
                onChange={(checked) => {
                  handleCheckboxChange(checked, allergy.code);
                }}
              >
                <div className="flex items-center gap-x-1">
                  <span> {allergy.title}</span>
                  <span className="scale-75 text-xs text-indigo-600 bg-indigo-100 border border-1 border-indigo-600 rounded-full p-1">
                    {allergy.code}
                  </span>
                </div>
              </CheckBoxInput>
            ))}
          </div>
          <div className="flex flex-col space-y-2">
            {allergiesColumns[1]?.map((allergy) => (
              <CheckBoxInput
                key={allergy.code}
                value={isAllergySelected(allergy.code)}
                onChange={(checked) => {
                  handleCheckboxChange(checked, allergy.code);
                }}
              >
                <div className="flex items-center gap-x-1">
                  <span> {allergy.title}</span>
                  <span className="scale-75 text-xs text-indigo-600 bg-indigo-100 border border-1 border-indigo-600 rounded-full p-1">
                    {allergy.code}
                  </span>
                </div>
              </CheckBoxInput>
            ))}
          </div>
        </div>
      </div>

      {errorMessage && <p className="text-red-500 text-sm mt-1 ml-0.5">{errorMessage}</p>}
    </div>
  );
}
